import React, { useState, useEffect } from 'react';
import { postDataAPI, getDataAPI, deleteDataAPI } from '../../../utils/fetchData';
import Modal from '../../../components/Modal';
import { useModal } from '../../../hooks/useModal';

const GuestlistTab = ({ show, onRefresh }) => {
  const { modalConfig, showModal, closeModal } = useModal();
  const [guests, setGuests] = useState(show.guestlist || []);
  const [showAddModal, setShowAddModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mode, setMode] = useState('select'); // 'select' or 'create'
  const [selectedGuests, setSelectedGuests] = useState([]);

  // Form state for creating new guest
  const [formData, setFormData] = useState({
    name: '',
    ticket_type: 'GA',
    party_size: 1,
    contactInfo: {
      phone: '',
      email: ''
    },
    notes: '',
    saveToContacts: true
  });

  useEffect(() => {
    if (showAddModal && mode === 'select') {
      loadContacts();
    }
  }, [showAddModal, mode]);

  const loadContacts = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const params = new URLSearchParams();
      params.append('role', 'Guest');
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await getDataAPI(`user/contacts?${params.toString()}`, token);
      if (response.data.status === 200) {
        setContacts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const handleCheckIn = async (guestId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI(`user/tm/guestlist/${guestId}/check-in`, {}, token);
      
      if (response.data.status === 200) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error checking in guest:', error);
      showModal('Error', 'Failed to check in guest', 'error');
    }
  };

  const handleAddFromContact = async (contact) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Add guest to show
      const guestData = {
        tourShowId: show._id,
        name: contact.name,
        guestType: 'GA',
        ticketCount: 1,
        plusOnes: 0,
        contactInfo: {
          phone: contact.contactInfo?.phone || '',
          email: contact.contactInfo?.email || ''
        },
        notes: contact.notes || ''
      };

      const response = await postDataAPI('user/tm/guestlist', guestData, token);
      
      if (response.data.status === 201) {
        // Update contact's last used timestamp
        await postDataAPI(`user/contacts/${contact._id}/update-last-used`, {}, token);
        
        setShowAddModal(false);
        onRefresh();
        showModal('Success', 'Guest added to list', 'success');
      }
    } catch (error) {
      console.error('Error adding guest:', error);
      showModal('Error', 'Failed to add guest', 'error');
    }
  };

  const handleCreateGuest = async () => {
    try {
      if (!formData.name) {
        showModal('Error', 'Name is required', 'error');
        return;
      }

      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // If saving to contacts, create contact first
      if (formData.saveToContacts) {
        const contactData = {
          name: formData.name,
          primaryRole: 'Guest',
          roles: ['Guest'],
          contactInfo: formData.contactInfo,
          notes: formData.notes
        };
        await postDataAPI('user/contacts', contactData, token);
      }

      // Add guest to show
      const guestData = {
        tourShowId: show._id,
        name: formData.name,
        guestType: formData.ticket_type,
        ticketCount: 1,
        plusOnes: formData.party_size - 1,
        contactInfo: formData.contactInfo,
        notes: formData.notes
      };

      const response = await postDataAPI('user/tm/guestlist', guestData, token);
      
      if (response.data.status === 201) {
        setShowAddModal(false);
        setFormData({
          name: '',
          ticket_type: 'GA',
          party_size: 1,
          contactInfo: { phone: '', email: '' },
          notes: '',
          saveToContacts: true
        });
        onRefresh();
        showModal('Success', 'Guest added successfully', 'success');
      }
    } catch (error) {
      console.error('Error creating guest:', error);
      showModal('Error', 'Failed to add guest', 'error');
    }
  };

  const handleDeleteGuest = (guest) => {
    showModal(
      'Remove Guest',
      `Remove ${guest.name} from guestlist?`,
      'warning',
      async () => {
        try {
          const token = JSON.parse(localStorage.getItem('dizeeToken'));
          await deleteDataAPI(`user/tm/guestlist/${guest._id}`, {}, token);
          onRefresh();
          closeModal();
        } catch (error) {
          console.error('Error deleting guest:', error);
          showModal('Error', 'Failed to remove guest', 'error');
        }
      },
      true
    );
  };

  const handleGuestSelect = (guestId) => {
    setSelectedGuests(prev => 
      prev.includes(guestId) 
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const handleBulkDeleteGuests = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Delete all selected guests
      await Promise.all(
        selectedGuests.map(guestId => 
          deleteDataAPI(`user/tm/guestlist/${guestId}`, {}, token)
        )
      );
      
      setSelectedGuests([]);
      onRefresh();
      showModal('Success', `${selectedGuests.length} guest(s) removed`, 'success');
    } catch (error) {
      console.error('Error deleting guests:', error);
      showModal('Error', 'Failed to remove guests', 'error');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'checked-in': 'bg-[#34C759] text-white',
      'vip': 'bg-[#FF9500] text-white',
      'ga': 'bg-[rgba(255,255,255,0.2)] text-white',
      'aa': 'bg-[rgba(255,255,255,0.15)] text-white'
    };
    return styles[status?.toLowerCase()] || styles.ga;
  };

  return (
    <div className="px-4 pb-6">
      {/* Selected Items Header */}
      {selectedGuests.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-[rgba(255,149,0,0.1)] border border-[#FF9500] rounded-lg">
          <span className="text-white text-[12px]">
            Selected ({selectedGuests.length})
          </span>
          <button
            onClick={handleBulkDeleteGuests}
            className="text-[#FF3B30] text-[12px] hover:opacity-70"
          >
            Delete
          </button>
        </div>
      )}

      {(!guests || guests.length === 0) ? (
        <div className="text-left py-[60px]">
          <h3 className="text-white text-[12px] font-semibold mb-2">
            No guests found
          </h3>
          <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
            Add guests to manage your guestlist for this show.
          </p>
          <button 
            onClick={() => {
              setMode('select');
              setShowAddModal(true);
            }}
            className="bg-[#FF9500] text-black px-5 py-2 rounded-lg text-[12px] hover:bg-[#FFB340] transition-colors"
          >
            Add Guest
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {guests.map((guest, index) => {
            const isSelected = selectedGuests.includes(guest._id || index);
            return (
              <div
                key={guest._id || index}
                className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 transition-all ${
                  isSelected 
                    ? 'border-[#FF9500] bg-[rgba(255,149,0,0.1)]' 
                    : 'border-[rgba(255,255,255,0.1)] hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)]'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleGuestSelect(guest._id || index)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded border-[rgba(255,255,255,0.3)] bg-transparent checked:bg-[#FF9500] checked:border-[#FF9500] focus:ring-[#FF9500] focus:ring-2"
                    />
                    <div className="flex-1">
                      <p className="text-white text-[12px] font-semibold mb-1">
                        {guest.name}
                      </p>
                    </div>
                  </div>
                  <button
                    className="text-[#FF9500] text-[10px] hover:opacity-70 font-medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle view guest - could navigate to detail page or open modal
                      console.log('View guest:', guest);
                    }}
                  >
                    View
                  </button>
                </div>
                
                <div className="pl-7">
                  <div className="flex items-center gap-2 text-[10px] text-[rgba(255,255,255,0.6)] mb-2">
                    <span className={`px-2 py-1 rounded ${getStatusBadge(guest.guestType || guest.ticket_type)}`}>
                      {(guest.guestType || guest.ticket_type || 'GA').toUpperCase()}
                    </span>
                    {(guest.plusOnes || guest.party_size) && (
                      <>
                        <span>•</span>
                        <span>+{guest.plusOnes || guest.party_size - 1}</span>
                      </>
                    )}
                    {guest.arrival_time && (
                      <>
                        <span>•</span>
                        <span>{guest.arrival_time}</span>
                      </>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-[rgba(255,255,255,0.5)] text-[10px]">
                      {guest.contactInfo?.email && (
                        <div>{guest.contactInfo.email}</div>
                      )}
                      {guest.contactInfo?.phone && (
                        <div>{guest.contactInfo.phone}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleCheckIn(guest._id)}
                      className={`px-3 py-1 rounded text-[10px] font-semibold transition-colors ${
                        guest.status === 'checked-in' || guest.checked_in
                          ? 'bg-[#34C759] text-white'
                          : 'bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]'
                      }`}
                    >
                      {guest.status === 'checked-in' || guest.checked_in ? 'Checked In' : 'Check-In'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Guest Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-2xl w-full max-w-[360px] max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#1a1a1a] p-4 border-b border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white text-[16px] font-semibold">Add Guest</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-white hover:opacity-70 text-[24px]"
                >
                  ×
                </button>
              </div>

              {/* Mode Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setMode('select')}
                  className={`flex-1 py-2 rounded-lg text-[11px] font-semibold transition-colors ${
                    mode === 'select'
                      ? 'bg-[#FF9500] text-white'
                      : 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] hover:bg-[rgba(255,255,255,0.15)]'
                  }`}
                >
                  From Contacts
                </button>
                <button
                  onClick={() => setMode('create')}
                  className={`flex-1 py-2 rounded-lg text-[11px] font-semibold transition-colors ${
                    mode === 'create'
                      ? 'bg-[#FF9500] text-white'
                      : 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] hover:bg-[rgba(255,255,255,0.15)]'
                  }`}
                >
                  Create New
                </button>
              </div>
            </div>

            <div className="p-4">
              {mode === 'select' ? (
                <>
                  {/* Search */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search contacts..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        loadContacts();
                      }}
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                    />
                  </div>

                  {/* Contacts List */}
                  <div className="space-y-2">
                    {contacts.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-[rgba(255,255,255,0.5)] text-[11px] mb-2">
                          No guest contacts found
                        </p>
                        <p className="text-[rgba(255,255,255,0.4)] text-[10px] mb-4">
                          Create contacts in the Contacts app first
                        </p>
                        <button
                          onClick={() => setMode('create')}
                          className="text-[#FF9500] text-[11px] hover:opacity-70"
                        >
                          Or create a new guest →
                        </button>
                      </div>
                    ) : (
                      contacts.map(contact => (
                        <button
                          key={contact._id}
                          onClick={() => handleAddFromContact(contact)}
                          className="w-full text-left bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 hover:bg-[rgba(255,255,255,0.08)] transition-colors"
                        >
                          <p className="text-white text-[12px] font-semibold mb-1">
                            {contact.name}
                          </p>
                          {contact.contactInfo?.email && (
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                              {contact.contactInfo.email}
                            </p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Create New Guest Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-[rgba(255,255,255,0.7)] text-[11px] block mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                        placeholder="Guest name"
                      />
                    </div>

                    <div>
                      <label className="text-[rgba(255,255,255,0.7)] text-[11px] block mb-1">
                        Ticket Type
                      </label>
                      <select
                        value={formData.ticket_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, ticket_type: e.target.value }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                      >
                        <option value="GA">GA</option>
                        <option value="VIP">VIP</option>
                        <option value="AA">AA</option>
                        <option value="Comp">Comp</option>
                        <option value="Industry">Industry</option>
                        <option value="Press">Press</option>
                        <option value="Family">Family</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[rgba(255,255,255,0.7)] text-[11px] block mb-1">
                        Party Size
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.party_size}
                        onChange={(e) => setFormData(prev => ({ ...prev, party_size: parseInt(e.target.value) || 1 }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                      />
                    </div>

                    <div>
                      <label className="text-[rgba(255,255,255,0.7)] text-[11px] block mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.contactInfo.email}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          contactInfo: { ...prev.contactInfo, email: e.target.value }
                        }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="text-[rgba(255,255,255,0.7)] text-[11px] block mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.contactInfo.phone}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          contactInfo: { ...prev.contactInfo, phone: e.target.value }
                        }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="text-[rgba(255,255,255,0.7)] text-[11px] block mb-1">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={2}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500] resize-none"
                        placeholder="Additional notes..."
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="saveToContacts"
                        checked={formData.saveToContacts}
                        onChange={(e) => setFormData(prev => ({ ...prev, saveToContacts: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <label htmlFor="saveToContacts" className="text-[rgba(255,255,255,0.7)] text-[11px]">
                        Save to Contacts for future use
                      </label>
                    </div>

                    <button
                      onClick={handleCreateGuest}
                      className="w-full bg-[#FF9500] text-white px-4 py-2 rounded-lg text-[12px] font-semibold hover:opacity-90"
                    >
                      Add Guest
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </div>
  );
};

export default GuestlistTab;
